import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { FileText, Plus, Stethoscope } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import type { QuoteTemplateSummary } from '@t3/shared';

interface StripeProbe {
  configured: boolean;
  publishableKeySet: boolean;
  mode: 'live' | 'test' | null;
  publishableMode?: 'live' | 'test' | null;
  keysAgree?: boolean;
  webhookSecretSet?: boolean;
  /** Everything set up, webhook included. */
  ok: boolean;
  /** A payment can be taken, whatever the webhook's state. */
  paymentsOk?: boolean;
  ms?: number;
  livemode?: boolean;
  webhook?: { ok: boolean; expectedUrl: string; found: boolean; enabled: boolean | null; missingEvents: string[]; apiVersion: string | null; detail: string };
  lastEvent?: { receivedAt: string; livemode: boolean | null } | null;
  problems?: string[];
  stripeType?: string | null;
  detail: string;
}

const probeWhen = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });

// The Quotes board: the pipeline of what we are quoting, and the place a
// quote is started. Quote-agent (and admin) only — a technician neither
// sees the nav item nor gets past the router gate.

// `requested`: a client asked for it from the portal and nobody has started it.
type QuoteState = 'requested' | 'draft' | 'sent' | 'accepted' | 'declined' | 'expired';

interface QuoteRow {
  ticketId: string;
  displayNumber: number;
  subject: string;
  organizationId: string;
  organizationName: string;
  requesterName: string | null;
  hasDocument: boolean;
  title: string | null;
  totalHours: number | null;
  totalAmountUsd: number | null;
  state: QuoteState;
  updatedAt: string;
}

interface Org {
  id: string;
  name: string;
}

const STATE_LABELS: Record<QuoteState, string> = {
  requested: 'Requested',
  draft: 'Being written',
  sent: 'With the client',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
};

const STATE_CLASSES: Record<QuoteState, string> = {
  requested: 'border-violet-200 bg-violet-50 text-violet-800',
  draft: 'border-[var(--color-border)] bg-white text-[var(--color-muted)]',
  sent: 'border-sky-200 bg-sky-50 text-sky-800',
  accepted: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  declined: 'border-rose-200 bg-rose-50 text-rose-800',
  expired: 'border-amber-200 bg-amber-50 text-amber-900',
};

const VIEWS: Array<{ key: string; label: string; match: (q: QuoteRow) => boolean }> = [
  {
    key: 'open',
    label: 'Open',
    match: (q) => q.state === 'requested' || q.state === 'draft' || q.state === 'sent',
  },
  { key: 'requested', label: 'Requested', match: (q) => q.state === 'requested' },
  { key: 'draft', label: 'Being written', match: (q) => q.state === 'draft' },
  { key: 'sent', label: 'With the client', match: (q) => q.state === 'sent' },
  { key: 'expired', label: 'Expired', match: (q) => q.state === 'expired' },
  { key: 'accepted', label: 'Accepted', match: (q) => q.state === 'accepted' },
  { key: 'declined', label: 'Declined', match: (q) => q.state === 'declined' },
  { key: 'all', label: 'All', match: () => true },
];

const money = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const NEW_CLIENT = '__new__';

const inputClass =
  'block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30';

export function QuotesListPage(): React.ReactElement {
  useDocumentTitle('Quotes');
  const navigate = useNavigate();
  const { agent } = useAuth();

  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState('open');

  const [creating, setCreating] = useState(false);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [orgId, setOrgId] = useState('');
  const [templates, setTemplates] = useState<QuoteTemplateSummary[]>([]);
  // "Test payments": the client-facing signing page is the one surface
  // here that depends on an outside service, so when it fails there has
  // to be a button that says why. Same idea as the Qwilr probe on the
  // contracts board.
  const [stripeProbe, setStripeProbe] = useState<StripeProbe | null>(null);
  const [probing, setProbing] = useState(false);
  const [templateId, setTemplateId] = useState<string>('');
  const [addingClient, setAddingClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    phone: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    addressCity: '',
    addressRegion: '',
    addressPostalCode: '',
    addressCountry: '',
  });
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  async function load(): Promise<void> {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiFetch<{ quotes: QuoteRow[] }>('/api/quote-documents');
      setQuotes(data.quotes);
    } catch (e) {
      setLoadError(
        e instanceof ApiError && e.status === 403
          ? 'Quotes are limited to quote agents. Ask an admin for the Quote agent role.'
          : 'Could not load quotes.',
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!creating || orgs.length > 0) return;
    apiFetch<{ organizations: Org[] }>('/api/organizations?isActive=true')
      .then((r) => setOrgs(r.organizations))
      .catch(() => setOrgs([]));
    apiFetch<{ templates: QuoteTemplateSummary[] }>('/api/quote-documents/templates')
      .then((r) => setTemplates(r.templates))
      .catch(() => setTemplates([]));
  }, [creating, orgs.length]);

  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const v of VIEWS) out[v.key] = quotes.filter(v.match).length;
    return out;
  }, [quotes]);

  const visible = useMemo(() => {
    const matcher = VIEWS.find((v) => v.key === view) ?? VIEWS[0]!;
    return quotes.filter(matcher.match);
  }, [quotes, view]);

  async function create(): Promise<void> {
    setBusy(true);
    setCreateError(null);
    try {
      let clientId = orgId;

      // The client is created first and separately. If the quote then
      // fails to start, the client still exists and is picked from the
      // list on the retry — better than losing both, and it is what
      // happens anyway if the page is closed mid-way.
      if (addingClient) {
        const created = await apiFetch<{ organization: { id: string; name: string } }>(
          '/api/organizations',
          {
            method: 'POST',
            json: {
              name: newClient.name.trim(),
              phone: newClient.phone.trim() || null,
              email: newClient.email.trim() || null,
              addressLine1: newClient.addressLine1.trim() || null,
              addressLine2: newClient.addressLine2.trim() || null,
              addressCity: newClient.addressCity.trim() || null,
              addressRegion: newClient.addressRegion.trim() || null,
              addressPostalCode: newClient.addressPostalCode.trim() || null,
              addressCountry: newClient.addressCountry.trim() || null,
            },
          },
        );
        clientId = created.organization.id;
        setOrgs((prev) =>
          [...prev, { id: created.organization.id, name: created.organization.name }].sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        );
        setAddingClient(false);
        setOrgId(clientId);
      }

      const data = await apiFetch<{ ticketId: string }>('/api/quote-documents', {
        method: 'POST',
        json: {
          organizationId: clientId,
          title: title.trim(),
          templateId: templateId || null,
        },
      });
      // Straight into the builder. Starting a quote and writing it are
      // one action as far as the person doing it is concerned.
      navigate(`/admin/quotes/${data.ticketId}`);
    } catch (e) {
      setCreateError(e instanceof ApiError ? e.message : 'Could not start the quote.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <Card>
        <h1 className="text-lg font-semibold text-[var(--color-header)]">Quotes</h1>
        <p className="mt-2 text-sm text-[var(--color-muted)]">{loadError}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-header)]">Quotes</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Scope, price and send work. Accepted quotes land on the contracts board.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              void (async () => {
                setProbing(true);
                try {
                  setStripeProbe(await apiFetch<StripeProbe>('/api/quote-documents/stripe/test'));
                } catch (e) {
                  setStripeProbe({
                    configured: false,
                    publishableKeySet: false,
                    mode: null,
                    ok: false,
                    detail:
                      e instanceof ApiError ? e.message : 'Could not reach the server to test.',
                  });
                } finally {
                  setProbing(false);
                }
              })();
            }}
            disabled={probing}
            title="Check that card payments on the client-facing quote page will work"
          >
            <Stethoscope className="h-4 w-4" aria-hidden="true" />
            {probing ? 'Testing…' : 'Test payments'}
          </Button>
          {!creating && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              New quote
            </Button>
          )}
        </div>
      </div>

      {stripeProbe && (
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-header)]">
                Card payments — {(stripeProbe.paymentsOk ?? stripeProbe.ok) ? 'working' : 'not working'}
                {typeof stripeProbe.livemode === 'boolean' && (
                  <span
                    className={
                      stripeProbe.livemode
                        ? 'ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800'
                        : 'ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900'
                    }
                  >
                    {stripeProbe.livemode ? 'LIVE' : 'TEST MODE'}
                  </span>
                )}
              </h2>
              <p className="mt-1 text-sm text-[var(--color-body)]">{stripeProbe.detail}</p>
              {stripeProbe.problems && stripeProbe.problems.length > 0 && (
                <ul className="mt-2 space-y-1 rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {stripeProbe.problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              )}
              <ul className="mt-2 space-y-1 text-sm text-[var(--color-muted)]">
                <li>
                  Secret key: {stripeProbe.configured ? `set (${stripeProbe.mode} mode)` : 'NOT SET'}
                </li>
                <li>
                  Publishable key:{' '}
                  {stripeProbe.publishableKeySet
                    ? `set${stripeProbe.publishableMode ? ` (${stripeProbe.publishableMode} mode)` : ''}`
                    : 'NOT SET'}
                </li>
                {typeof stripeProbe.webhookSecretSet === 'boolean' && (
                  <li>Webhook signing secret: {stripeProbe.webhookSecretSet ? 'set' : 'NOT SET'}</li>
                )}
                {stripeProbe.webhook && (
                  <li>
                    Webhook endpoint ({stripeProbe.webhook.expectedUrl}):{' '}
                    {stripeProbe.webhook.found
                      ? `${stripeProbe.webhook.enabled ? 'enabled' : 'disabled'}${
                          stripeProbe.webhook.missingEvents.length > 0
                            ? `, missing ${stripeProbe.webhook.missingEvents.join(', ')}`
                            : ', all events'
                        }${stripeProbe.webhook.apiVersion ? ` · API ${stripeProbe.webhook.apiVersion}` : ''}`
                      : 'not found in Stripe'}
                  </li>
                )}
                {stripeProbe.lastEvent !== undefined && (
                  <li>
                    Last Stripe event received:{' '}
                    {stripeProbe.lastEvent
                      ? `${probeWhen(stripeProbe.lastEvent.receivedAt)}${
                          stripeProbe.lastEvent.livemode === null ? '' : stripeProbe.lastEvent.livemode ? ' (live)' : ' (test)'
                        }`
                      : 'none yet'}
                  </li>
                )}
                {typeof stripeProbe.ms === 'number' && <li>Round trip: {stripeProbe.ms} ms</li>}
                {stripeProbe.stripeType && <li>Stripe error type: {stripeProbe.stripeType}</li>}
              </ul>
              {!(stripeProbe.paymentsOk ?? stripeProbe.ok) && (
                <p className="mt-2 text-sm text-[var(--color-muted)]">
                  Until this reads &quot;working&quot;, a quote that asks for a deposit cannot be
                  accepted — the client sees a message saying payment is unavailable rather than a
                  broken page.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setStripeProbe(null)}
              className="text-sm text-[var(--color-muted)] hover:underline"
            >
              Dismiss
            </button>
          </div>
        </Card>
      )}

      {creating && (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Start a quote</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Pick what you are selling, then the client and a name. The scope and pricing come next
            and are yours to change — a template is a starting point, never a link back.
          </p>

          <fieldset className="mt-4">
            <legend className="mb-2 text-sm font-medium text-[var(--color-header)]">
              Start from
            </legend>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTemplateId(t.id)}
                  aria-pressed={templateId === t.id}
                  className={`rounded-[var(--radius-button)] border p-3 text-left transition ${
                    templateId === t.id
                      ? 'border-[var(--color-t3-blue)] bg-[var(--color-t3-blue)]/5'
                      : 'border-[var(--color-border)] bg-white hover:border-[var(--color-t3-blue)]'
                  }`}
                >
                  <span className="block text-sm font-semibold text-[var(--color-header)]">
                    {t.name}
                  </span>
                  {t.description && (
                    <span className="mt-1 block text-xs text-[var(--color-muted)]">
                      {t.description}
                    </span>
                  )}
                  <span className="mt-2 block text-xs text-[var(--color-muted)]">
                    {t.sectionCount} section{t.sectionCount === 1 ? '' : 's'}
                    {t.lineItemCount > 0
                      ? ` · ${t.totalHours} h · ${money(t.totalAmountUsd)}`
                      : ' · no default pricing'}
                  </span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setTemplateId('')}
                aria-pressed={templateId === ''}
                className={`rounded-[var(--radius-button)] border border-dashed p-3 text-left transition ${
                  templateId === ''
                    ? 'border-[var(--color-t3-blue)] bg-[var(--color-t3-blue)]/5'
                    : 'border-[var(--color-border)] bg-white hover:border-[var(--color-t3-blue)]'
                }`}
              >
                <span className="block text-sm font-semibold text-[var(--color-header)]">
                  Blank
                </span>
                <span className="mt-1 block text-xs text-[var(--color-muted)]">
                  One empty section. Use Custom SOW instead if you want the full house structure.
                </span>
              </button>
            </div>
          </fieldset>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Client
              </span>
              <select
                value={addingClient ? NEW_CLIENT : orgId}
                onChange={(e) => {
                  if (e.target.value === NEW_CLIENT) {
                    setAddingClient(true);
                    setOrgId('');
                  } else {
                    setAddingClient(false);
                    setOrgId(e.target.value);
                  }
                }}
                className={inputClass}
              >
                <option value="">Pick a client…</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
                {/* A quote is often the first thing that happens with a
                    prospect, so the client frequently does not exist yet.
                    Sending someone to Clients to make one and come back
                    loses the quote they were halfway through starting. */}
                <option value={NEW_CLIENT}>+ Add a client…</option>
              </select>
            </label>

            {addingClient && (
              <div className="sm:col-span-2 rounded-[var(--radius-button)] border border-[var(--color-t3-blue)]/40 bg-[var(--color-t3-blue)]/5 p-4">
                <h3 className="text-sm font-semibold text-[var(--color-header)]">New client</h3>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Only the name is required. The phone, email and address are what a quote, a
                  signed SOW and an invoice are addressed to, so they are worth filling in now if
                  you have them.
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="block sm:col-span-2">
                    <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                      Company name
                    </span>
                    <input
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                      className={inputClass}
                      placeholder="Acme Corp"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                      Company phone
                    </span>
                    <input
                      value={newClient.phone}
                      onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                      className={inputClass}
                      placeholder="(555) 123-4567"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                      Company email
                    </span>
                    <input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      className={inputClass}
                      placeholder="accounts@acme.com"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                      Address
                    </span>
                    <input
                      value={newClient.addressLine1}
                      onChange={(e) => setNewClient({ ...newClient, addressLine1: e.target.value })}
                      className={inputClass}
                      placeholder="123 Main Street"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="sr-only">Address line 2</span>
                    <input
                      value={newClient.addressLine2}
                      onChange={(e) => setNewClient({ ...newClient, addressLine2: e.target.value })}
                      className={inputClass}
                      placeholder="Suite 400 (optional)"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                      City
                    </span>
                    <input
                      value={newClient.addressCity}
                      onChange={(e) => setNewClient({ ...newClient, addressCity: e.target.value })}
                      className={inputClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                      State / region
                    </span>
                    <input
                      value={newClient.addressRegion}
                      onChange={(e) => setNewClient({ ...newClient, addressRegion: e.target.value })}
                      className={inputClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                      ZIP / postal code
                    </span>
                    <input
                      value={newClient.addressPostalCode}
                      onChange={(e) =>
                        setNewClient({ ...newClient, addressPostalCode: e.target.value })
                      }
                      className={inputClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                      Country
                    </span>
                    <input
                      value={newClient.addressCountry}
                      onChange={(e) =>
                        setNewClient({ ...newClient, addressCountry: e.target.value })
                      }
                      className={inputClass}
                      placeholder="United States"
                    />
                  </label>
                </div>
              </div>
            )}
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Quote name
              </span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputClass}
                placeholder="Firewall refresh — phase 1"
              />
            </label>
          </div>
          {createError && <p className="mt-3 text-sm text-[var(--color-danger)]">{createError}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              onClick={() => void create()}
              disabled={
                busy ||
                !title.trim() ||
                (addingClient ? !newClient.name.trim() : !orgId)
              }
            >
              {busy ? 'Starting…' : 'Start writing'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                setCreateError(null);
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => setView(v.key)}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
              view === v.key
                ? 'border-[var(--color-t3-blue)] bg-[var(--color-t3-blue)] text-white'
                : 'border-[var(--color-border)] bg-white text-[var(--color-body)] hover:border-[var(--color-t3-blue)]'
            }`}
          >
            {v.label}
            <span className={view === v.key ? 'ml-1.5 text-white/80' : 'ml-1.5 text-[var(--color-muted)]'}>
              {counts[v.key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">Loading quotes…</p>
        </Card>
      ) : visible.length === 0 ? (
        <Card>
          <div className="py-8 text-center">
            <FileText className="mx-auto h-6 w-6 text-[var(--color-muted)]" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-[var(--color-header)]">
              Nothing here yet
            </p>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              {view === 'open'
                ? 'No quotes in flight. Start one with New quote.'
                : 'No quotes in this view.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <th className="px-3 py-2.5 font-semibold">Quote</th>
                <th className="px-3 py-2.5 font-semibold">Client</th>
                <th className="px-3 py-2.5 font-semibold">State</th>
                <th className="px-3 py-2.5 text-right font-semibold">Hours</th>
                <th className="px-3 py-2.5 text-right font-semibold">Value</th>
                <th className="px-3 py-2.5 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((q) => (
                <tr
                  key={q.ticketId}
                  className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-surface)]"
                >
                  <td className="px-3 py-2.5">
                    <Link
                      to={`/admin/quotes/${q.ticketId}`}
                      className="font-medium text-[var(--color-t3-blue)] hover:underline"
                    >
                      {q.title ?? q.subject}
                    </Link>
                    <span className="ml-2 text-xs text-[var(--color-muted)]">
                      T-{q.displayNumber}
                    </span>
                    {q.state === 'requested' ? (
                      <span className="ml-2 text-xs text-[var(--color-muted)]">
                        · asked for{q.requesterName ? ` by ${q.requesterName}` : ''} in the portal
                      </span>
                    ) : (
                      !q.hasDocument && (
                        <span className="ml-2 text-xs text-[var(--color-muted)]">· not written</span>
                      )
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[var(--color-body)]">{q.organizationName}</td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${STATE_CLASSES[q.state]}`}
                    >
                      {STATE_LABELS[q.state]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[var(--color-body)]">
                    {q.totalHours === null ? '—' : q.totalHours}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[var(--color-body)]">
                    {q.totalAmountUsd === null ? '—' : money(q.totalAmountUsd)}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-[var(--color-muted)]">
                    {new Date(q.updatedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <Link
          to="/admin/quotes/templates"
          className="text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
        >
          Manage templates →
        </Link>
        {agent?.role === 'admin' && (
          <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
            ← Back to admin
          </Link>
        )}
      </div>
    </div>
  );
}
